import React, { useCallback, useEffect, useRef, useState } from 'react'
import { SafeAreaView } from 'react-native-safe-area-context'
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs'
import { ClientMonitoringType } from '@/domain/models'
import { PrintEvents, GetMoniEvents, GetRemotyEvents, GetSowillEvents, GetSigmaEvents, GetRemotySectors, GetMoniSectors, GetRemotyUsers, GetSowillUsers, GetRemotyContacts, GetSowillSectors, GetMoniUsers, GetSigmaCloudSectors, GetSigmaCloudUsers, GetSigmaCloudKeys, GetSowillKeys, GetSowillIdentifier, GetSigmaDeskSectors, GetSigmaDeskContacts } from '@/domain/usecases'
import { DateAdapter } from '@/data/protocols'
import { NoResultMessage } from '@/presentation/components'
import { useAuth, useThemeClient, useMediaQueries, useNotify } from '@/presentation/hooks'
import { SideFilters, SectorList, UserEventList, ContactEventList, MonitoringEventList, sowillKeysState, sowillIdentifierState, sigmaKeysState, selectSigmaCloudKeyData, remotyFilterState, isLoadingMonitoringEventState, isLoadingSectorEventsState, isLoadingUserEventsState, isLoadingContactEventState, monitoringDateFiltersState, sigmaFilterState, lastLoadedMonitoringEventsKeyState, lastLoadedSectorKeyState, lastLoadedUserKeyState, lastLoadedContactKeyState } from '@/presentation/pages/Events/components'
import { styles } from './styles'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { useFocusEffect } from '@react-navigation/native'
import { AccessDeniedError } from '@/domain/errors'
import { selectSowillKeyState } from './components/SelectSowillKey/atom'
import { buildMonitoringEventsRequestKey, buildMonitoringTabRequestKey } from './request-key'

const Tab = createMaterialTopTabNavigator()
const MONITORING_TAB_ROUTE = {
  EVENTS: 'events',
  SECTORS: 'sectors',
  USERS: 'users',
  CONTACTS: 'contacts'
} as const

type EventsProps = {
  dateAdapter: DateAdapter
  printEvents: PrintEvents
  getRemotyEvents: GetRemotyEvents
  getRemotySectors: GetRemotySectors
  getRemotyUsers: GetRemotyUsers
  getRemotyContacts: GetRemotyContacts
  getMoniEvents: GetMoniEvents
  getMoniSectors: GetMoniSectors
  getMoniUsers: GetMoniUsers
  getSowillEvents: GetSowillEvents
  getSowillSectors: GetSowillSectors
  getSowillUsers: GetSowillUsers
  getSowillKeys: GetSowillKeys
  getSowillIdentifier: GetSowillIdentifier
  getSigmaEvents: GetSigmaEvents
  getSigmaCloudSectors: GetSigmaCloudSectors
  getSigmaCloudUsers: GetSigmaCloudUsers
  getSigmaCloudKeys: GetSigmaCloudKeys
  getSigmaDeskSectors: GetSigmaDeskSectors
  getSigmaDeskContacts: GetSigmaDeskContacts
}

export const Events: React.FC<EventsProps> = ({
  dateAdapter,
  printEvents,
  getSigmaEvents,
  getRemotyEvents,
  getRemotySectors,
  getRemotyUsers,
  getRemotyContacts,
  getMoniEvents,
  getMoniSectors,
  getMoniUsers,
  getSowillEvents,
  getSowillSectors,
  getSowillUsers,
  getSowillKeys,
  getSowillIdentifier,
  getSigmaCloudSectors,
  getSigmaCloudUsers,
  getSigmaCloudKeys,
  getSigmaDeskSectors,
  getSigmaDeskContacts
}) => {
  const { isMobile } = useMediaQueries()
  const { getThemeClient } = useThemeClient()
  const { getClient, logout } = useAuth()
  const [sigmaKeys, setSigmaKeys] = useRecoilState(sigmaKeysState)

  const setSowillKeysList = useSetRecoilState(sowillKeysState)
  const setMonitoringLoading = useSetRecoilState(isLoadingMonitoringEventState)
  const setSectorLoading = useSetRecoilState(isLoadingSectorEventsState)
  const setUserLoading = useSetRecoilState(isLoadingUserEventsState)
  const setContactLoading = useSetRecoilState(isLoadingContactEventState)
  const selectedSowillKey = useRecoilValue(selectSowillKeyState)
  const currentSigmaKey = useRecoilValue(selectSigmaCloudKeyData)
  const { remotyIdEquipamento } = useRecoilValue(remotyFilterState)
  const sowillIdentifier = useRecoilValue(sowillIdentifierState)
  const setSowillIdentifier = useSetRecoilState(sowillIdentifierState)
  const monitoringFilters = useRecoilValue(monitoringDateFiltersState)
  const sigmaFilter = useRecoilValue(sigmaFilterState)
  const lastLoadedMonitoringEventsKey = useRecoilValue(lastLoadedMonitoringEventsKeyState)
  const lastLoadedSectorKey = useRecoilValue(lastLoadedSectorKeyState)
  const lastLoadedUserKey = useRecoilValue(lastLoadedUserKeyState)
  const lastLoadedContactKey = useRecoilValue(lastLoadedContactKeyState)
  const hasFocusedMonitoringRef = useRef(false)
  const [tabNavigatorKey, setTabNavigatorKey] = useState(0)

  const themeClient = getThemeClient()
  const client = getClient()
  const notify = useNotify()

  const errorHandler = (error: Error): void => {
    notify.show(error.message, { variant: 'error' })

    if (error instanceof AccessDeniedError) {
      logout()
    }
  }

  useFocusEffect(
    useCallback(() => {
      if (hasFocusedMonitoringRef.current) {
        setTabNavigatorKey((currentKey) => currentKey + 1)
      } else {
        hasFocusedMonitoringRef.current = true
      }

      if (
        [ClientMonitoringType.SIGMA_CLOUD, ClientMonitoringType.SIGMA_DESK]
          .includes(client.tipoConexao) &&
        !sigmaKeys.length) {
        getSigmaCloudKeys
          .getKeys({ codCliente: client.codCliente })
          .then(setSigmaKeys)
          .catch(errorHandler)
      } else
        if ([ClientMonitoringType.SO_WILL].includes(client.tipoConexao)) {
          getSowillKeys
            .getKeys({ codCliente: client.codCliente, unidade: client.unidade })
            .then(setSowillKeysList)
            .catch(errorHandler)
        }
    }, [])
  )

  useEffect(() => {
    if ([ClientMonitoringType.SO_WILL].includes(client.tipoConexao) &&
      selectedSowillKey.chave
    ) {
      getSowillIdentifier
        .getIdentifier(selectedSowillKey)
        .then(setSowillIdentifier)
        .catch(errorHandler)
    }
  }, [selectedSowillKey.chave])

  const hasMonitoringSelection = (): boolean => ({
    [ClientMonitoringType.REMOTY]: remotyIdEquipamento !== 0,
    [ClientMonitoringType.MONI]: client.tipoConexao === ClientMonitoringType.MONI,
    [ClientMonitoringType.SIGMA_CLOUD]: !!currentSigmaKey.codCliente,
    [ClientMonitoringType.SIGMA_DESK]: !!currentSigmaKey.codCliente,
    [ClientMonitoringType.SO_WILL]: !!sowillIdentifier.codInternoChave
  }[client.tipoConexao])

  const hasContactSelection = (): boolean => ({
    [ClientMonitoringType.REMOTY]: remotyIdEquipamento !== 0,
    [ClientMonitoringType.MONI]: false,
    [ClientMonitoringType.SIGMA_CLOUD]: false,
    [ClientMonitoringType.SIGMA_DESK]: !!currentSigmaKey.codCliente,
    [ClientMonitoringType.SO_WILL]: false
  }[client.tipoConexao])

  const monitoringTabRequestKey = buildMonitoringEventsRequestKey({
    clientType: client.tipoConexao,
    currentSigmaKey,
    dateAdapter,
    filters: monitoringFilters,
    remotyIdEquipamento,
    sowillIdentifier,
    sigmaFilterType: sigmaFilter.sigmaTipo
  })

  const sectorTabRequestKey = buildMonitoringTabRequestKey({
    clientType: client.tipoConexao,
    currentSigmaKey,
    remotyIdEquipamento,
    sowillIdentifier
  })

  const userTabRequestKey = buildMonitoringTabRequestKey({
    clientType: client.tipoConexao,
    currentSigmaKey,
    remotyIdEquipamento,
    sowillIdentifier
  })

  const contactTabRequestKey = buildMonitoringTabRequestKey({
    clientType: client.tipoConexao,
    currentSigmaKey,
    remotyIdEquipamento,
    sowillIdentifier
  })

  const createTabPressListener = (
    setLoading: (value: boolean) => void,
    hasSelection: () => boolean,
    isStale: () => boolean
  ): { tabPress: () => void } => ({
    tabPress: (): void => {
      if (hasSelection() && isStale()) {
        setLoading(true)
      }
    }
  })

  return (
    <SafeAreaView style={styles.container}>
      <SideFilters
        dateAdapter={dateAdapter}
      />
      {client.tipoConexao != null
        ? <Tab.Navigator
          key={`monitoring-tabs-${tabNavigatorKey}`}
          initialRouteName={MONITORING_TAB_ROUTE.EVENTS}
          screenOptions={{
            tabBarItemStyle: { paddingHorizontal: 40 },
            tabBarLabelStyle: { color: themeClient.menuColor },
            tabBarIndicatorStyle: { backgroundColor: themeClient.menuColor },
            tabBarStyle: { backgroundColor: themeClient.mainColor }
          }}
        >
          <Tab.Screen
            name={MONITORING_TAB_ROUTE.EVENTS}
            options={{
              tabBarLabel: isMobile ? 'Eventos' : 'Eventos do alarme'
            }}
            listeners={createTabPressListener(
              setMonitoringLoading,
              hasMonitoringSelection,
              () => monitoringTabRequestKey !== lastLoadedMonitoringEventsKey
            )}
          >
            {() => (
              <MonitoringEventList
                dateAdapter={dateAdapter}
                printEvents={printEvents}
                getMoniEvents={getMoniEvents}
                getSigmaEvents={getSigmaEvents}
                getRemotyEvents={getRemotyEvents}
                getSowillEvents={getSowillEvents}
                getSowillIdentifier={getSowillIdentifier}
              />
            )}
          </Tab.Screen>

          {
            [
              ClientMonitoringType.REMOTY,
              ClientMonitoringType.MONI,
              ClientMonitoringType.SO_WILL,
              ClientMonitoringType.SIGMA_CLOUD,
              ClientMonitoringType.SIGMA_DESK
            ]
              .includes(client.tipoConexao) &&
            <Tab.Screen
              name={MONITORING_TAB_ROUTE.SECTORS}
              options={{
                tabBarLabel: [ClientMonitoringType.SO_WILL].includes(client.tipoConexao)
                  ? 'Zonas'
                  : 'Setores'
              }}
              listeners={createTabPressListener(
                setSectorLoading,
                hasMonitoringSelection,
                () => sectorTabRequestKey !== lastLoadedSectorKey
              )}
            >
              {() => (
                <SectorList
                  getRemotySectors={getRemotySectors}
                  getMoniSectors={getMoniSectors}
                  getSowillSectors={getSowillSectors}
                  getSigmaCloudSectors={getSigmaCloudSectors}
                  getSigmaDeskSectors={getSigmaDeskSectors}
                />
              )}
            </Tab.Screen>
          }
          {
            [
              ClientMonitoringType.REMOTY,
              ClientMonitoringType.MONI,
              ClientMonitoringType.SO_WILL,
              ClientMonitoringType.SIGMA_CLOUD
            ]
              .includes(client.tipoConexao) &&
            <Tab.Screen
              name={MONITORING_TAB_ROUTE.USERS}
              options={{
                tabBarLabel: 'Usuarios'
              }}
              listeners={createTabPressListener(
                setUserLoading,
                hasMonitoringSelection,
                () => userTabRequestKey !== lastLoadedUserKey
              )}
            >
              {() => (
                <UserEventList
                  getRemotyUsers={getRemotyUsers}
                  getMoniUsers={getMoniUsers}
                  getSowillUsers={getSowillUsers}
                  getSigmaCloudUsers={getSigmaCloudUsers}
                />
              )}
            </Tab.Screen>
          }
          {
            [
              ClientMonitoringType.REMOTY,
              ClientMonitoringType.SIGMA_DESK
            ]
              .includes(client.tipoConexao) &&
            <Tab.Screen
              name={MONITORING_TAB_ROUTE.CONTACTS}
              options={{
                tabBarLabel: 'Contatos'
              }}
              listeners={createTabPressListener(
                setContactLoading,
                hasContactSelection,
                () => contactTabRequestKey !== lastLoadedContactKey
              )}
            >
              {() => (
                <ContactEventList
                  getRemotyContacts={getRemotyContacts}
                  getSigmaDeskContacts={getSigmaDeskContacts}
                />
              )}
            </Tab.Screen>
          }

        </Tab.Navigator>
        : <NoResultMessage text='Cliente não possui tipo de integração de monitoramento' />
      }
    </SafeAreaView>
  )
}
